import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { OrderData } from '../page';

type SatisfactionState = 'idle' | 'satisfied' | 'unsatisfied' | 'submitted';

interface Props {
  orderData: OrderData | null;
}

const METHOD_CONFIG: Record<string, { color: string; label: string; icon: string }> = {
  card:       { color: '#22C55E', label: 'بطاقة عبر Stripe',    icon: 'ri-bank-card-line' },
  apple_pay:  { color: '#22C55E', label: 'Apple Pay',            icon: 'ri-apple-fill' },
  google_pay: { color: '#22C55E', label: 'Google Pay',           icon: 'ri-google-fill' },
  tabby:      { color: '#3DBDA7', label: 'Tabby — تقسيط',       icon: 'ri-exchange-dollar-line' },
  cod:        { color: '#C9A961', label: 'الدفع عند الاستلام',   icon: 'ri-cash-line' },
};

const STEPS = ['تأكيد الطلب', 'تجهيز الطلب', 'في الطريق', 'تم التوصيل'];

export default function ConfirmStep({ orderData }: Props) {
  const [copied, setCopied] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [satisfaction, setSatisfaction] = useState<SatisfactionState>('idle');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const method = orderData?.method ?? 'cod';
  const cfg = METHOD_CONFIG[method] ?? METHOD_CONFIG.cod;
  const isPaid = method !== 'cod' && method !== 'tabby';
  const isTabby = method === 'tabby';

  const orderId      = orderData?.orderId      ?? `#${Math.floor(Math.random() * 90000 + 10000)}`;
  const trackingNum  = orderData?.trackingNumber ?? `TR-${Date.now().toString(36).toUpperCase()}-0000`;
  const items        = orderData?.items          ?? [];
  const form         = orderData?.form;
  const total        = orderData?.total         ?? 0;
  const shipping     = orderData?.shipping      ?? 0;
  const subtotal     = total - shipping;
  const installment  = Math.ceil(total / 4);

  const orderDate    = orderData?.timestamp
    ? new Date(orderData.timestamp).toLocaleDateString('ar-AE', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : new Date().toLocaleDateString('ar-AE', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingNum).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // entrance animation
  useEffect(() => {
    const t = setTimeout(() => setShowItems(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-[70vh] flex items-start justify-center py-6" dir="rtl">
      <div
        className={`w-full max-w-3xl transition-all duration-500 ${showItems ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >

        {/* ===== SUCCESS HERO ===== */}
        <div className="bg-white rounded-3xl p-8 mb-4 text-center">
          {/* Icon */}
          <div
            className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-4"
            style={{ background: isPaid ? '#DCFCE7' : isTabby ? '#3DBDA718' : '#C9A96118' }}
          >
            <i
              className={`text-4xl ${isPaid ? 'ri-checkbox-circle-fill text-green-500' : isTabby ? 'ri-time-line text-[#3DBDA7]' : 'ri-checkbox-circle-fill text-[#C9A961]'}`}
            />
          </div>

          {/* Badge */}
          <div
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full mb-3"
            style={{
              background: isPaid ? '#DCFCE7' : isTabby ? '#3DBDA718' : '#C9A96118',
              color: isPaid ? '#16A34A' : isTabby ? '#3DBDA7' : '#C9A961',
            }}
          >
            {isTabby
              ? <i className="ri-loader-4-line animate-spin text-sm" />
              : <i className="ri-checkbox-circle-line text-sm" />
            }
            {isPaid ? 'تم الدفع بنجاح ✓' : isTabby ? 'بانتظار تأكيد Tabby' : 'تم تسجيل طلبك ✓'}
          </div>

          <h2 className="text-2xl font-black text-gray-900 mb-1">
            {isPaid ? 'تم الدفع وتسجيل طلبك!' : isTabby ? 'طلبك مسجل بنجاح' : 'طلبك مسجل بنجاح!'}
          </h2>
          <p className="text-gray-400 text-sm mb-6">{orderDate}</p>

          {/* Tracking Number — Prominent */}
          <div className="bg-[#F5F1E8] rounded-2xl px-5 py-4 mb-4">
            <p className="text-xs text-gray-400 mb-2 font-semibold">رقم تتبع الطلب</p>
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                  copied ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 hover:bg-gray-100'
                }`}
              >
                <i className={copied ? 'ri-checkbox-circle-line' : 'ri-file-copy-line'} />
                {copied ? 'تم النسخ!' : 'نسخ'}
              </button>
              <span className="font-black text-gray-900 text-lg tracking-widest" dir="ltr">
                {trackingNum}
              </span>
            </div>
          </div>

          {/* Order ID row */}
          <div className="flex items-center justify-between px-2">
            <span className="text-gray-400 text-sm">رقم الطلب</span>
            <span className="font-black text-gray-900" dir="ltr">{orderId}</span>
          </div>
        </div>

        {/* ===== PROGRESS STEPPER ===== */}
        <div className="bg-white rounded-3xl px-8 py-6 mb-4">
          <h3 className="font-black text-gray-900 mb-5 text-sm">حالة الطلب</h3>
          <div className="relative flex justify-between items-start">
            <div className="absolute top-[13px] right-3.5 left-3.5 h-0.5 bg-gray-100" />
            <div
              className="absolute top-[13px] right-3.5 h-0.5"
              style={{
                background: cfg.color,
                width: `calc((100% - 1.75rem) / ${STEPS.length - 1})`,
              }}
            />
            {STEPS.map((s, i) => {
              const done   = i === 0;
              const active = i === 1;
              return (
                <div key={i} className="relative z-10 flex flex-col items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-black transition-all ${
                      done ? 'text-white' : active ? 'bg-white border-2' : 'bg-gray-100 text-gray-300'
                    }`}
                    style={
                      done   ? { background: cfg.color }
                      : active ? { borderColor: cfg.color, color: cfg.color }
                      : {}
                    }
                  >
                    {done ? <i className="ri-check-line text-xs" /> : i + 1}
                  </div>
                  <span
                    className={`text-[10px] font-bold text-center leading-tight ${
                      done || active ? 'text-gray-700' : 'text-gray-300'
                    }`}
                  >
                    {s}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== ITEMS + CUSTOMER INFO 2-COL ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">

          {/* LEFT: Items list */}
          <div className="lg:col-span-3 bg-white rounded-3xl p-6">
            <h3 className="font-black text-gray-900 mb-4 text-sm flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-[#C9A961]" />
              </div>
              المنتجات المطلوبة
              <span className="text-gray-400 font-normal text-xs">({items.length} منتج)</span>
            </h3>

            {items.length > 0 ? (
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
                    {item.image && (
                      <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 text-sm font-bold truncate">{item.name}</p>
                      <p className="text-gray-400 text-xs mt-0.5">الكمية: {item.quantity}</p>
                    </div>
                    <div className="text-left flex-shrink-0">
                      <p className="font-black text-gray-900 text-sm">{(item.price * item.quantity).toFixed(0)} د.إ</p>
                      {item.quantity > 1 && (
                        <p className="text-gray-400 text-xs">{item.price} × {item.quantity}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-300">
                <i className="ri-shopping-bag-3-line text-3xl mb-2 block" />
                <p className="text-sm">لا توجد تفاصيل</p>
              </div>
            )}

            {/* Price breakdown */}
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-900 font-semibold">{subtotal} د.إ</span>
                <span className="text-gray-400">المنتجات</span>
              </div>
              <div className="flex justify-between text-sm">
                {shipping === 0
                  ? <span className="text-green-600 font-semibold">مجاني</span>
                  : <span className="text-gray-900 font-semibold">{shipping} د.إ</span>
                }
                <span className="text-gray-400">الشحن</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="font-black text-xl" style={{ color: cfg.color }}>{total} د.إ</span>
                <span className="font-black text-gray-900">الإجمالي</span>
              </div>
            </div>

            {/* Tabby installments */}
            {isTabby && (
              <div className="mt-4 bg-[#3DBDA7]/8 rounded-2xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-bold text-right">جدول الأقساط</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {['اليوم', 'شهر 1', 'شهر 2', 'شهر 3'].map((label, i) => (
                    <div
                      key={i}
                      className={`rounded-xl py-2 px-1 text-center border ${i === 0 ? 'bg-[#3DBDA7] border-[#3DBDA7]' : 'bg-white border-[#3DBDA7]/30'}`}
                    >
                      <div className={`text-[10px] ${i === 0 ? 'text-white/70' : 'text-gray-400'}`}>{label}</div>
                      <div className={`text-xs font-black ${i === 0 ? 'text-white' : 'text-[#3DBDA7]'}`}>{installment} د.إ</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Customer info + Payment */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Delivery info */}
            {form && (
              <div className="bg-white rounded-3xl p-5 flex-1">
                <h3 className="font-black text-gray-900 mb-4 text-sm flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-[#C9A961]" />
                  </div>
                  بيانات التوصيل
                </h3>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-user-line',      label: 'الاسم',    val: form.name },
                    { icon: 'ri-whatsapp-line',  label: 'الهاتف',   val: form.phone },
                    { icon: 'ri-map-2-line',     label: 'الإمارة',  val: form.city },
                    { icon: 'ri-home-4-line',    label: 'العنوان',  val: form.address },
                    ...(form.email ? [{ icon: 'ri-mail-line', label: 'البريد', val: form.email }] : []),
                  ].map(row => (
                    <div key={row.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#F5F1E8] rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${row.icon} text-[#C9A961] text-sm`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium">{row.label}</p>
                        <p className="text-sm font-bold text-gray-800">{row.val}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment method badge */}
            <div className="bg-white rounded-3xl p-5">
              <h3 className="font-black text-gray-900 mb-3 text-sm flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-bank-card-line text-[#C9A961]" />
                </div>
                طريقة الدفع
              </h3>
              <div
                className="flex items-center gap-3 p-3 rounded-2xl"
                style={{ background: cfg.color + '15' }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: cfg.color + '25' }}
                >
                  <i className={`${cfg.icon} text-lg`} style={{ color: cfg.color }} />
                </div>
                <div>
                  <p className="font-bold text-sm text-gray-800">{cfg.label}</p>
                  <p className="text-xs" style={{ color: cfg.color }}>
                    {isPaid ? 'تم الدفع بنجاح' : isTabby ? 'بانتظار التأكيد' : 'يُدفع عند الاستلام'}
                  </p>
                </div>
              </div>

              {/* Delivery estimate */}
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F5F1E8] mt-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white flex-shrink-0">
                  <i className="ri-truck-line text-[#C9A961] text-lg" />
                </div>
                <div>
                  <p className="font-bold text-sm text-gray-800">التوصيل المتوقع</p>
                  <p className="text-xs text-gray-400">خلال 2–5 أيام عمل</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== NOTICE FOR TABBY ===== */}
        {isTabby && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-right">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500 text-lg" />
            </div>
            <p className="text-amber-700 text-xs leading-relaxed">
              <strong>تنبيه:</strong> لم يُخصم أي مبلغ بعد. ستتواصل معك Tabby لتفعيل التقسيط وخصم الدفعة الأولى.
            </p>
          </div>
        )}

        {/* ===== SATISFACTION SURVEY ===== */}
        <div className="bg-white rounded-3xl p-6 mb-4 text-center" dir="rtl">
          <h3 className="font-black text-gray-900 mb-1 text-base">هل أنت راضٍ عن تجربتك؟</h3>
          <p className="text-gray-400 text-xs mb-5">رأيك يهمنا ويساعدنا على التحسين</p>

          {!feedbackSubmitted ? (
            <>
              {/* Emoji buttons */}
              <div className="flex justify-center gap-4 mb-5">
                {/* Satisfied */}
                <button
                  onClick={() => setSatisfaction(satisfaction === 'satisfied' ? 'idle' : 'satisfied')}
                  className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
                    satisfaction === 'satisfied'
                      ? 'border-green-400 bg-green-50 scale-105'
                      : 'border-gray-100 bg-gray-50 hover:border-green-200 hover:bg-green-50/50'
                  }`}
                >
                  <span className="text-4xl">😊</span>
                  <span className={`text-xs font-bold ${satisfaction === 'satisfied' ? 'text-green-600' : 'text-gray-500'}`}>
                    راضٍ
                  </span>
                </button>

                {/* Unsatisfied */}
                <button
                  onClick={() => setSatisfaction(satisfaction === 'unsatisfied' ? 'idle' : 'unsatisfied')}
                  className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
                    satisfaction === 'unsatisfied'
                      ? 'border-red-400 bg-red-50 scale-105'
                      : 'border-gray-100 bg-gray-50 hover:border-red-200 hover:bg-red-50/50'
                  }`}
                >
                  <span className="text-4xl">😞</span>
                  <span className={`text-xs font-bold ${satisfaction === 'unsatisfied' ? 'text-red-500' : 'text-gray-500'}`}>
                    غير راضٍ
                  </span>
                </button>
              </div>

              {/* Feedback text area — shows when a choice is made */}
              {satisfaction !== 'idle' && (
                <div className="text-right mb-4 animate-in fade-in duration-200">
                  <label className="text-xs text-gray-500 font-semibold mb-1.5 block">
                    {satisfaction === 'satisfied'
                      ? 'ما الذي أعجبك؟ (اختياري)'
                      : 'أخبرنا بما يمكن تحسينه'}
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={e => setFeedbackText(e.target.value.slice(0, 300))}
                    rows={3}
                    placeholder={
                      satisfaction === 'satisfied'
                        ? 'مثلاً: التوصيل كان سريعاً، المنتج رائع...'
                        : 'مثلاً: التوصيل تأخر، المنتج لم يطابق الوصف...'
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-gray-400 transition-colors"
                  />
                  <p className="text-[10px] text-gray-300 text-left mt-1">{feedbackText.length}/300</p>
                </div>
              )}

              {/* Submit button */}
              {satisfaction !== 'idle' && (
                <button
                  onClick={() => setFeedbackSubmitted(true)}
                  className="px-8 py-3 rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all text-white"
                  style={{ background: satisfaction === 'satisfied' ? '#22C55E' : '#EF4444' }}
                >
                  إرسال التقييم
                </button>
              )}
            </>
          ) : (
            /* Thank you state */
            <div className="py-4 animate-in fade-in duration-300">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-3"
                style={{ background: satisfaction === 'satisfied' ? '#DCFCE7' : '#FEE2E2' }}
              >
                <span className="text-3xl">{satisfaction === 'satisfied' ? '🙏' : '💪'}</span>
              </div>
              <p className="font-black text-gray-900 text-base mb-1">
                {satisfaction === 'satisfied' ? 'شكراً لك! يسعدنا رضاك 🎉' : 'شكراً على صراحتك!'}
              </p>
              <p className="text-gray-400 text-xs">
                {satisfaction === 'satisfied'
                  ? 'نتطلع لخدمتك مجدداً قريباً'
                  : 'سنعمل على تحسين تجربتك في الطلبات القادمة'}
              </p>
              {feedbackText && (
                <div
                  className="mt-4 rounded-xl px-4 py-3 text-sm text-right"
                  style={{
                    background: satisfaction === 'satisfied' ? '#F0FDF4' : '#FFF5F5',
                    color: satisfaction === 'satisfied' ? '#16A34A' : '#DC2626',
                  }}
                >
                  <i className="ri-chat-quote-line ml-1" />
                  &quot;{feedbackText}&quot;
                </div>
              )}
            </div>
          )}
        </div>

        {/* ===== ACTIONS ===== */}
        <div className="bg-white rounded-3xl p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              to={`/track-order?order=${orderId.replace('#', '')}`}
              className="flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold cursor-pointer whitespace-nowrap transition-all border-2 text-gray-700 hover:bg-[#F5F1E8]"
              style={{ borderColor: cfg.color + '50' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-truck-line" style={{ color: cfg.color }} />
              </div>
              تتبّع طلبك
            </Link>
            <Link
              to="/products"
              className="flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold cursor-pointer whitespace-nowrap transition-all text-white"
              style={{ background: cfg.color }}
            >
              <i className="ri-store-2-line" />
              مواصلة التسوق
            </Link>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3.5 rounded-2xl font-bold cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              الرئيسية
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
